import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  isAuthenticated,
  updateStudent,
} from "../../../../ApiService/ApiService";
import { isValidAadhar } from "../../../../Utils/validFormDetails";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchClassesAsync,
  selectAllClasses,
  selectClassesLoading,
  selectClassesError,
} from "../../../../Slices/classesSlice";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../../Components/CustomDialog";

function StudentInfo({
  setActionComponent,
  students,
  selectedStudentId,
  fetchStudentsByClassId,
}) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchClassesAsync());
  }, [dispatch]);

  const classes = useSelector(selectAllClasses);
  const classesLoading = useSelector(selectClassesLoading);
  const classesError = useSelector(selectClassesError);

  const studentsDataFromId =
    students.data.find((student) => student.id === selectedStudentId) || {};

  const [aadharNumber, setAadharNumber] = useState(
    studentsDataFromId.aadharNumber || ""
  );
  const [classId, setClassId] = useState(studentsDataFromId.classId || "");
  const [loading, setLoading] = useState(false);

  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const [confirmDialog, setConfirmDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ ...confirmDialog, visible: false });
  };

  const handleConfirmSubmit = async () => {
    handleConfirmDialogClose();
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await updateStudent({
          aadharNumber,
          classId,
          studentId: selectedStudentId,
        });
        console.log("Update Student Info", response?.data);
        if (response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Student's information updated",
            buttons: [{ name: "OK", onPress: handleDialogClose }],
          });
          fetchStudentsByClassId(studentsDataFromId.classId);
          setAadharNumber(response?.data?.aadharNumber);
          setClassId(response?.data?.classId);
          setActionComponent(false);
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!aadharNumber || !classId) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please fill in all the required details.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (!isValidAadhar(aadharNumber)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid aadhar number.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    setConfirmDialog({
      visible: true,
      title: "Confirmation",
      message:
        "If you update student class, you will have to attach new fees to student tuition fee as per new grade/class. Want to proceed?",
      buttons: [
        { name: "No", onPress: handleConfirmDialogClose },
        { name: "Yes", onPress: handleConfirmSubmit },
      ],
    });
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <CustomDialog
        visible={confirmDialog.visible}
        title={confirmDialog.title}
        message={confirmDialog.message}
        buttons={confirmDialog.buttons}
        onClose={handleConfirmDialogClose}
      />
      <Box sx={{ maxWidth: "100%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography>
                  Registration Id: {studentsDataFromId.registrationId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography>
                  First Name: {studentsDataFromId.firstName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography>
                  Middle Name: {studentsDataFromId.middleName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography>
                  Last Name: {studentsDataFromId.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography>Gender: {studentsDataFromId.gender}</Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography>DOB: {studentsDataFromId.dob}</Typography>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography>
                  Date of Joining: {studentsDataFromId.doj}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Aadhar No."
                  name="aadharNumber"
                  value={aadharNumber}
                  onChange={(e) => setAadharNumber(e.target.value)}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="class">Class</InputLabel>
                  <Select
                    name="classId"
                    label="Class"
                    labelId="class"
                    value={classId}
                    onChange={(e) => setClassId(e.target.value)}
                    size="small"
                    disabled={classesLoading || classesError}
                  >
                    {classesLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : classesError ? (
                      <MenuItem value="">Error fetching classes</MenuItem>
                    ) : (
                      classes?.data?.map((classItem) => (
                        <MenuItem key={classItem.id} value={classItem.id}>
                          {classItem.grade} - {classItem.section}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setActionComponent(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default StudentInfo;
